import React, { useState } from "react";
import { Button, Tooltip, OverlayTrigger } from "react-bootstrap";
import moment from "moment";
import "./ProjectPartnerTab.css";

const ProjectPartnerTab = ({
  // Data
  partners,
  partnersLoading,
  vendorMap,
  vendorContactsMap,
  loadingVendorContacts,

  // Permissions
  hasProjectAdminRole,
  hasProjectManagerRole,

  // Handlers
  handleCreatePartner,
  handleEditPartner,
}) => {
  const canEdit = hasProjectAdminRole() || hasProjectManagerRole();
  const [hoveredContact, setHoveredContact] = useState(null);

  // Function to get initials from contact name
  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Function to get random pastel color based on name
  const getContactColor = (name) => {
    const colors = [
      "#6366f1",
      "#8b5cf6",
      "#ec4899",
      "#f43f5e",
      "#ef4444",
      "#f59e0b",
      "#eab308",
      "#84cc16",
      "#10b981",
      "#14b8a6",
      "#06b6d4",
      "#0ea5e9",
      "#3b82f6",
      "#6b7280",
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Render contact tooltip content
  const renderContactTooltip = (contact) => (
    <Tooltip id={`tooltip-${contact.cusVenId}`} className="ppt-contact-tooltip">
      <div className="ppt-tooltip-content">
        <div className="ppt-tooltip-name">{contact.contactPerName}</div>
        {contact.contactPerNumber && (
          <div className="ppt-tooltip-detail">
            <i className="fas fa-phone"></i> {contact.contactPerNumber}
          </div>
        )}
        {contact.contactPerEmail && (
          <div className="ppt-tooltip-detail">
            <i className="fas fa-envelope"></i> {contact.contactPerEmail}
          </div>
        )}
        {contact.designation && (
          <div className="ppt-tooltip-detail">
            <i className="fas fa-briefcase"></i> {contact.designation}
          </div>
        )}
        {contact.department && (
          <div className="ppt-tooltip-detail">
            <i className="fas fa-building"></i> {contact.department}
          </div>
        )}
      </div>
    </Tooltip>
  );

  return (
    <div className="ppt-container ppt-fade-in">
      {/* Stats Section */}
      <div className="ppt-stats">
        <div className="ppt-stat-item">
          <span className="ppt-stat-value">{partners.length}</span>
          <span className="ppt-stat-label">Total Partners</span>
        </div>
        <div className="ppt-stat-divider"></div>
        <div className="ppt-stat-item">
          <span className="ppt-stat-value">
            {
              partners.filter(
                (p) => vendorContactsMap[p.partnerName]?.length > 0,
              ).length
            }
          </span>
          <span className="ppt-stat-label">With Contacts</span>
        </div>
        <div className="ppt-header-right">
          {canEdit && (
            <Button className="ppt-btn-primary" onClick={handleCreatePartner}>
              <i className="fas fa-plus"></i>
              Add Partner
            </Button>
          )}
        </div>
      </div>

      {partnersLoading ? (
        <div className="ppt-loading">
          <div className="ppt-spinner" role="status">
            <span className="visually-hidden">Loading partners...</span>
          </div>
          <p>Loading partners...</p>
        </div>
      ) : partners.length === 0 ? (
        <div className="ppt-empty">
          <div className="ppt-empty-icon">
            <i className="fas fa-handshake"></i>
          </div>
          <h3>No Partners Found</h3>
          <p>No partners are associated with this project yet.</p>
          {canEdit && (
            <Button className="ppt-btn-outline" onClick={handleCreatePartner}>
              <i className="fas fa-plus me-2"></i>
              Add Your First Partner
            </Button>
          )}
        </div>
      ) : (
        <div className="ppt-table-wrapper">
          <table className="ppt-table">
            <thead className="ppt-table-header">
              <tr>
                <th>Partner ID</th>
                <th>Name</th>
                <th>Contact Persons</th>
                <th>Created By</th>
                <th>Created Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody className="ppt-table-body">
              {partners.map((partner) => (
                <tr key={partner.partnerId}>
                  <td>
                    <div className="ppt-partner-id">{partner.partnerId}</div>
                  </td>
                  <td>
                    <div className="ppt-partner-name">
                      {vendorMap[partner.partnerName]
                        ? `${vendorMap[partner.partnerName].name}`
                        : partner.partnerName}
                      {vendorMap[partner.partnerName]?.vendorCode && (
                        <span className="ppt-partner-code">
                          ({vendorMap[partner.partnerName].vendorCode})
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    {loadingVendorContacts[partner.partnerName] ? (
                      <div className="ppt-skeleton">
                        <div className="ppt-skeleton-name"></div>
                        <div className="ppt-skeleton-text"></div>
                      </div>
                    ) : vendorContactsMap[partner.partnerName]?.length > 0 ? (
                      <div className="ppt-contacts-avatars">
                        {vendorContactsMap[partner.partnerName].map(
                          (cp, index) => (
                            <OverlayTrigger
                              key={cp.cusVenId}
                              placement="top"
                              delay={{ show: 250, hide: 400 }}
                              overlay={renderContactTooltip(cp)}
                            >
                              <div
                                className="ppt-contact-avatar"
                                style={{
                                  backgroundColor: getContactColor(
                                    cp.contactPerName,
                                  ),
                                  zIndex:
                                    vendorContactsMap[partner.partnerName]
                                      .length - index,
                                }}
                              >
                                <span className="ppt-contact-initials">
                                  {getInitials(cp.contactPerName)}
                                </span>
                              </div>
                            </OverlayTrigger>
                          ),
                        )}
                        {vendorContactsMap[partner.partnerName].length > 3 && (
                          <div className="ppt-contact-avatar ppt-contact-more">
                            <span className="ppt-contact-initials">
                              +
                              {vendorContactsMap[partner.partnerName].length -
                                3}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="ppt-no-contacts">
                        <i className="fas fa-user-slash"></i> No contacts
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="ppt-created-by">
                      <i className="fas fa-user-circle"></i>
                      <span>{partner.createdBy || "-"}</span>
                    </div>
                  </td>
                  <td>
                    <div className="ppt-created-date">
                      {partner.createdOnDate
                        ? moment(partner.createdOnDate).format("MMM DD, YYYY")
                        : "-"}
                    </div>
                  </td>
                  <td>
                    <div className="ppt-actions">
                      {canEdit && (
                        <Button
                          className="ppt-action-btn edit"
                          size="sm"
                          onClick={() => handleEditPartner(partner)}
                          title="Edit Partner"
                        >
                          <i className="fas fa-edit"></i>
                          Edit
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProjectPartnerTab;

// import React from "react";
// import { Button, Badge } from "react-bootstrap";
// import moment from "moment";

// const ProjectPartnerTab = ({
//   // Data
//   partners,
//   partnersLoading,
//   vendorMap,
//   vendorContactsMap,
//   loadingVendorContacts,

//   // Permissions
//   hasProjectAdminRole,
//   hasProjectManagerRole,

//   // Handlers
//   handleCreatePartner,
//   handleEditPartner,
// }) => {
//   const canEdit = hasProjectAdminRole() || hasProjectManagerRole();

//   return (
//     <div className="project-view-fade-in">
//       <div className="project-overview-single-card">
//         <div className="d-flex justify-content-between align-items-center mb-4">
//           <div>
//             <h3 className="project-view-overview-section-title mb-1">
//               Project Partners
//             </h3>
//             <p className="text-muted mb-0">
//               Manage partners associated with this project
//             </p>
//           </div>
//           {canEdit && (
//             <Button variant="primary" onClick={handleCreatePartner}>
//               Add Partner
//             </Button>
//           )}
//         </div>

//         {partnersLoading ? (
//           <div className="text-center py-4">
//             <div className="spinner-border text-primary" role="status">
//               <span className="visually-hidden">Loading partners...</span>
//             </div>
//           </div>
//         ) : partners.length === 0 ? (
//           <div className="text-center py-5 text-muted">
//             <i className="fas fa-handshake fa-3x mb-3"></i>
//             <h5>No Partners Found</h5>
//             <p className="mb-4">
//               No partners are associated with this project yet.
//             </p>
//             {canEdit && (
//               <Button variant="outline-primary" onClick={handleCreatePartner}>
//                 <i className="fas fa-plus me-2"></i>
//                 Add Your First Partner
//               </Button>
//             )}
//           </div>
//         ) : (
//           <div className="table-responsive">
//             <table className="project-view-partner-table">
//               <thead className="project-view-partner-table-header">
//                 <tr>
//                   <th>Partner ID</th>
//                   <th>Name</th>
//                   <th>Contact Persons</th>
//                   <th>Created By</th>
//                   <th>Created Date</th>
//                   <th>Actions</th>
//                 </tr>
//               </thead>
//               <tbody className="project-view-partner-table-body">
//                 {partners.map((partner) => (
//                   <tr key={partner.partnerId} className="partner-table-row">
//                     <td className="partner-id-cell">
//                       <div className="partner-id">{partner.partnerId}</div>
//                     </td>
//                     <td className="partner-name-cell">
//                       <div className="partner-name">
//                         {vendorMap[partner.partnerName]
//                           ? `${vendorMap[partner.partnerName].name} (${vendorMap[partner.partnerName].vendorCode})`
//                           : partner.partnerName}
//                       </div>
//                     </td>
//                     <td className="partner-contacts-cell">
//                       {loadingVendorContacts[partner.partnerName] ? (
//                         <small className="text-muted">Loading...</small>
//                       ) : vendorContactsMap[partner.partnerName]?.length > 0 ? (
//                         <ul className="list-unstyled mb-0">
//                           {vendorContactsMap[partner.partnerName].map((cp) => (
//                             <li key={cp.cusVenId} className="mb-1">
//                               <strong>{cp.contactPerName}</strong>
//                               <br />
//                               <small className="text-muted">
//                                 📞 {cp.contactPerNumber || "-"} | ✉️{" "}
//                                 {cp.contactPerEmail || "-"}
//                               </small>
//                             </li>
//                           ))}
//                         </ul>
//                       ) : (
//                         <small className="text-muted">No contacts</small>
//                       )}
//                     </td>
//                     <td className="partner-createdby-cell">
//                       <div className="partner-createdby">
//                         {partner.createdBy || "-"}
//                       </div>
//                     </td>
//                     <td className="partner-date-cell">
//                       {partner.createdOnDate
//                         ? moment(partner.createdOnDate).format("MMM DD, YYYY")
//                         : "-"}
//                     </td>
//                     <td className="partner-actions-cell">
//                       <div className="partner-actions">
//                         {canEdit && (
//                           <Button
//                             variant="outline-primary"
//                             size="sm"
//                             onClick={() => handleEditPartner(partner)}
//                             className="me-2"
//                             title="Edit Partner"
//                           >
//                             Edit
//                           </Button>
//                         )}
//                       </div>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default ProjectPartnerTab;
